import * as THREE from "three";

/**
 * Renders the scene into a tiny target (PSX resolution), then upscales with nearest sampling and
 * quantises to 15-bit colour with an ordered 4×4 Bayer dither — the crosshatch in every gradient.
 */
const VERT = /* glsl */ `varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;
const FRAG = /* glsl */ `
precision mediump float;
uniform sampler2D tDiffuse; uniform vec2 uRes; uniform float uLevels;
varying vec2 vUv;
float bayer(vec2 p) {
  int x = int(mod(p.x, 4.0)); int y = int(mod(p.y, 4.0));
  int i = x + y * 4;
  // 4x4 Bayer matrix, normalised
  float m[16];
  m[0]=0.0; m[1]=8.0; m[2]=2.0; m[3]=10.0; m[4]=12.0; m[5]=4.0; m[6]=14.0; m[7]=6.0;
  m[8]=3.0; m[9]=11.0; m[10]=1.0; m[11]=9.0; m[12]=15.0; m[13]=7.0; m[14]=13.0; m[15]=5.0;
  for (int k = 0; k < 16; k++) { if (k == i) return (m[k] + 0.5) / 16.0; }
  return 0.5;
}
void main(){
  vec2 px = floor(vUv * uRes);                  // low-res pixel coordinate
  vec3 c = texture2D(tDiffuse, (px + 0.5) / uRes).rgb;
  float d = (bayer(px) - 0.5) * 0.85;
  c = floor(c * uLevels + d) / uLevels;          // 6 bits per channel + ordered dither
  gl_FragColor = vec4(c, 1.0);
}`;

export class PsxComposer {
  readonly target: THREE.WebGLRenderTarget;
  private quad: THREE.Mesh;
  private scene = new THREE.Scene();
  private cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  constructor(readonly renderer: THREE.WebGLRenderer, readonly width: number, readonly height: number) {
    this.target = new THREE.WebGLRenderTarget(width, height, { magFilter: THREE.NearestFilter, minFilter: THREE.NearestFilter, depthBuffer: true });
    const mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: { tDiffuse: { value: this.target.texture }, uRes: { value: new THREE.Vector2(width, height) }, uLevels: { value: 63.0 } } });
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
    this.scene.add(this.quad);
  }
  render(scene: THREE.Scene, camera: THREE.Camera): void {
    const r = this.renderer;
    r.setRenderTarget(this.target); r.clear(); r.render(scene, camera);
    r.setRenderTarget(null); r.render(this.scene, this.cam);
  }
  dispose(): void { this.target.dispose(); (this.quad.material as THREE.Material).dispose(); this.quad.geometry.dispose(); }
}
